// host.ts — the ONE seam between the pi chat pipeline and its embedding app.
//
// pi-view.ts needs exactly two live values from whoever hosts it: the active
// terminal color palette and the terminal appearance prefs (both feed
// piCssVars → the `--pi-*` custom-property set). In helm-bridge those come
// from appearance.ts/store.ts; in helm-console from its own prefs plumbing.
// Neither app's state module belongs inside this package, so the host
// registers the accessors at startup:
//
//   configurePiViewHost({
//     activePalette: () => appearance.activePalette(),
//     terminalPrefs: () => store.state.prefs.terminal,
//   });
//
// The default host answers with a built-in dark palette + default prefs so a
// view constructed before configuration renders legibly instead of throwing;
// hosts are still expected to configure at module-load time (helm-bridge does,
// in app.ts).

import { defaultTerminalPrefs, type TerminalColors, type TerminalPrefs } from './term-types.js';

export interface PiViewHost {
  /** The palette pi views theme from — the host resolves the active app
   * theme's chat palette. */
  activePalette(): TerminalColors;
  /** The terminal appearance prefs (font family/size/weights, chat font). */
  terminalPrefs(): TerminalPrefs;
}

/** Built-in fallback palette (a neutral dark scheme) — used only until the
 * host calls configurePiViewHost. Not exported: nothing should style against
 * it on purpose. */
const FALLBACK_PALETTE: TerminalColors = {
  background: '#1e1e1e',
  foreground: '#d4d4d4',
  cursor: '#d4d4d4',
  cursorAccent: '#1e1e1e',
  selectionBackground: '#264f78',
  black: '#000000',
  red: '#cd3131',
  green: '#0dbc79',
  yellow: '#e5e510',
  blue: '#2472c8',
  magenta: '#bc3fbc',
  cyan: '#11a8cd',
  white: '#e5e5e5',
  brightBlack: '#666666',
  brightRed: '#f14c4c',
  brightGreen: '#23d18b',
  brightYellow: '#f5f543',
  brightBlue: '#3b8eea',
  brightMagenta: '#d670d6',
  brightCyan: '#29b8db',
  brightWhite: '#e5e5e5',
};

let host: PiViewHost = {
  activePalette: () => FALLBACK_PALETTE,
  terminalPrefs: () => defaultTerminalPrefs(),
};

/** Install the embedding app's accessors. Call once at startup, before any
 * pi view mounts (helm-bridge: app.ts module scope). */
export function configurePiViewHost(h: PiViewHost): void {
  host = h;
}

/** The current host — read at render time, never cached, so a late
 * configure or a pref change is picked up by the next applyAppearance. */
export function piViewHost(): PiViewHost {
  return host;
}
